// apps/client/src/ui/screens/pockets.tsx (M1-T13) — the HUD's Pockets: a 78cqw panel, slots and detail at 1.55fr : 1fr
// (DESIGN.md "HUD (landscape phone)"). The hand slot sits above the 20 pocket slots while you hold something. Until a slot is picked
// the detail column says what to do; a picked fruit says how to plant it (M1-P1).
import { getEntry, tuning } from '@whiskerton/content';
import type { ItemView } from '@whiskerton/shared';
import { useShallow } from 'zustand/react/shallow';
import { useCommands } from '../../game/instance';
import { useGame } from '../../state/store';
import { useT } from '../i18n';
import { ItemDetail } from '../items/ItemDetail';
import { SlotGrid } from '../items/SlotGrid';
import { useIcon } from '../items/useIcon';
import { Button, Panel, Slot } from '../kit';
import type { ScreenDef } from '../router';

declare module '../router' { interface ScreenParams { pockets: undefined } }

/** A note in the detail column (M1 playtest, phone: with nothing picked the column stood empty, and nothing said a fruit could be
 *  planted): a cream well with centred text, like an empty slot. */
const HINT = { margin: 0, padding: '12px 16px', borderRadius: 16, background: 'var(--cream-100)', boxShadow: 'var(--sh-well)', color: 'var(--wood-700)',
  fontWeight: 800, textAlign: 'center', lineHeight: 1.35 } as const;

function Pockets() {
  const t = useT();
  const cmd = useCommands();
  const { you, space, tool, selected, select, openScreen } = useGame(useShallow((s) => ({ you: s.you, space: s.space, tool: s.tool,
    selected: s.inventorySelected, select: s.inventorySelect, openScreen: s.openScreen })));
  const items = you?.items ?? [];
  const pockets = items.filter((i) => i.at.loc === 'pockets');
  const hand = items.find((i) => i.at.loc === 'hand');
  const sel: ItemView | undefined = items.find((i) => i.id === selected);
  const entry = sel && getEntry(sel.catalogId);
  const handIcon = useIcon(hand?.catalogId ?? null);
  const atHome = !!you && space.startsWith(`house:${you.resident}:`);
  return (
    <Panel title={t('items.pockets')} className="pockets-panel">
      <div className="pockets-grid" style={{ display: 'grid', gridTemplateColumns: '1.55fr 1fr', gap: '2.4cqw', width: '78cqw', maxWidth: '100%' }}>
        <div style={{ display: 'grid', gap: '1.4cqw', alignContent: 'start' }}>
          {hand && (
            <div style={{ width: 'calc((100% - 4.4cqw) / 5)' }}>
              <Slot label={t('items.hand')} selected={selected === hand.id} onClick={() => select(hand.id)}><img src={handIcon} alt="" /></Slot>
            </div>
          )}
          <SlotGrid items={pockets} slots={tuning.items.pocketSlots} columns={5} selected={selected} onPick={(i) => select(i?.id ?? null)} />
        </div>
        <div style={{ display: 'grid', gap: 12, alignContent: 'start' }}>
          {sel && entry && entry.kind !== 'world' && (
            <ItemDetail item={sel}>
              {entry.kind === 'tool' && (
                <Button onClick={() => void cmd.holdTool(tool === sel.catalogId ? null : sel.catalogId)}>{t(tool === sel.catalogId ? 'items.unhold' : 'items.hold')}</Button>
              )}
              {space === 'out' && <Button onClick={() => void cmd.dropItem(sel.id)}>{t('items.drop')}</Button>}
              {sel.at.loc === 'hand' && pockets.length < tuning.items.pocketSlots && (
                <Button onClick={() => void cmd.moveItem(sel.id, 'pockets', null)}>{t('items.toPockets')}</Button>
              )}
              {entry.kind === 'fish' && sel.at.loc === 'hand' && <Button variant="quiet" onClick={() => void cmd.releaseFish(sel.id)}>{t('items.release')}</Button>}
              {atHome && sel.at.loc === 'pockets' && <Button variant="quiet" onClick={() => void cmd.moveItem(sel.id, 'storage', null)}>{t('items.toStorage')}</Button>}
            </ItemDetail>
          )}
          {sel && entry?.kind === 'fruit' && sel.at.loc === 'pockets' && <p className="pockets-hint" style={{ ...HINT, background: 'var(--leaf-100)' }}>{t('items.plantHint')}</p>}
          {!sel && <p className="pockets-hint" style={{ ...HINT, padding: '2.4cqw 16px' }}>{t(pockets.length === 0 && !hand ? 'items.empty' : 'items.pickHint')}</p>}
          {atHome && <Button variant="quiet" onClick={() => openScreen('storage', undefined)}>{t('items.storage')}</Button>}
        </div>
      </div>
    </Panel>
  );
}
export const screen: ScreenDef<'pockets'> = { id: 'pockets', layer: 'panel', component: () => <Pockets /> };
